/**
 * MarkdownMessage — renders AI chat replies (Kimani / Obondo) as proper
 * headings/paragraphs/lists/bold instead of raw `**`/`##`/`-` markdown
 * symbols (user-reported bug, Sept 2026). Scoped-down version of the
 * platform's Global Design System typography scale — chat bubbles are
 * small, so headings step up in *weight*, not to page-heading sizes.
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const components = {
    h1: ({ children }) => <p className="text-sm font-extrabold mt-2 mb-1 first:mt-0">{children}</p>,
    h2: ({ children }) => <p className="text-sm font-bold mt-2 mb-1 first:mt-0">{children}</p>,
    h3: ({ children }) => <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mt-2 mb-1 first:mt-0">{children}</p>,
    p:  ({ children }) => <p className="leading-relaxed mb-2 last:mb-0">{children}</p>,
    ul: ({ children }) => <ul className="list-disc pl-4 space-y-0.5 mb-2 last:mb-0">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-4 space-y-0.5 mb-2 last:mb-0">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    a:  ({ children, href }) => <a href={href} target="_blank" rel="noreferrer" className="underline text-primary">{children}</a>,
    code: ({ children }) => <code className="bg-black/10 dark:bg-white/10 rounded px-1 py-0.5 text-[0.9em] font-mono">{children}</code>,
    blockquote: ({ children }) => <blockquote className="border-l-2 border-current/30 pl-2 italic opacity-90 mb-2 last:mb-0">{children}</blockquote>,
    hr: () => <hr className="my-2 border-current/20" />,
};

export default function MarkdownMessage({ text, className = "" }) {
    if (!text) return null;
    return (
        <div className={className}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {text}
            </ReactMarkdown>
        </div>
    );
}
